import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { normalizeImageUrl } from '../utils/normalizeImageUrl';
import { getApiBase } from '../utils/apiBase';
import { dedupedFetchJson, invalidateDedupCache } from '../utils/apiDedup';

const API_BASE = getApiBase() || '';

const DEFAULTS = {
  company_name: 'Funstay Travels',
  logo_url: '/funstay-logo-color.png',
  email: '',
  phone: '',
  address: '',
  branding: {
    app_name: 'Agency OS',
    login_title: 'Agency OS',
    login_subtitle: 'Travel Agency Operating System',
  },
};

const DEFAULT_VALUE = {
  profile: DEFAULTS, loaded: true, refresh: () => {},
  logoUrl: DEFAULTS.logo_url, appName: DEFAULTS.branding.app_name,
  loginTitle: DEFAULTS.branding.login_title, loginSubtitle: DEFAULTS.branding.login_subtitle,
  companyName: DEFAULTS.company_name,
  favicon: '',
  ensureFaviconLoaded: async () => '',
};

const AgencyProfileContext = createContext(DEFAULT_VALUE);

export const AgencyProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [favicon, setFavicon] = useState('');

  const loadAgencyProfile = useCallback(async (force = false) => {
    const profileUrl = `${API_BASE}/api/settings/agency-profile`;
    if (force) invalidateDedupCache(profileUrl);
    const data = await dedupedFetchJson(profileUrl);
    if (data) {
      setProfile(prev => ({
        ...prev,
        ...data,
        branding: { ...prev.branding, ...(data.branding || {}) },
      }));
    }
    return data;
  }, []);

  const ensureFaviconLoaded = useCallback(async (force = false) => {
    const settingsUrl = `${API_BASE}/api/cms/settings`;
    if (!force && favicon) return favicon;
    if (force) invalidateDedupCache(settingsUrl);
    try {
      const data = await dedupedFetchJson(settingsUrl);
      const nextFavicon = data?.logos?.favicon || '';
      setFavicon(nextFavicon);
      return nextFavicon;
    } catch {
      return '';
    }
  }, [favicon]);

  useEffect(() => {
    loadAgencyProfile()
      .finally(() => setLoaded(true));
  }, [loadAgencyProfile]);

  const refresh = async () => {
    try {
      await loadAgencyProfile(true);
      await ensureFaviconLoaded(true);
    } catch {}
  };

  // Normalize logo URL to handle cross-fork domain changes
  const logoUrl = normalizeImageUrl(profile.logo_url) || DEFAULTS.logo_url;
  const appName = profile.branding?.app_name || DEFAULTS.branding.app_name;
  const loginTitle = profile.branding?.login_title || DEFAULTS.branding.login_title;
  const loginSubtitle = profile.branding?.login_subtitle || DEFAULTS.branding.login_subtitle;
  const companyName = profile.company_name || DEFAULTS.company_name;

  return (
    <AgencyProfileContext.Provider value={{ profile, loaded, refresh, logoUrl, appName, loginTitle, loginSubtitle, companyName, favicon, ensureFaviconLoaded }}>
      {children}
    </AgencyProfileContext.Provider>
  );
};

export const useAgencyProfile = () => {
  return useContext(AgencyProfileContext);
};
